import * as React from "react";

import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord } from "@/shared/domain/views";
import { useViews } from "@/shared/domain/views/hooks";

/**
 * `loadedView` with the name the View list has for it now.
 *
 * The loaded record is the one the View was opened from. A rename changes
 * nothing the table shows, so nothing replaces that record, but the list
 * refetches after one. For display only: everything else about the record is
 * left as it was loaded.
 */
export function useLatestLoadedView(
  target: QueryTarget,
  loadedView: ViewRecord | null,
): ViewRecord | null {
  const { data: views } = useViews(target);
  return React.useMemo(() => {
    if (loadedView === null) {
      return null;
    }
    const latest = views?.find((v) => v.view_id === loadedView.view_id);
    return latest === undefined || latest.name === loadedView.name
      ? loadedView
      : { ...loadedView, name: latest.name };
  }, [loadedView, views]);
}
